import React, { useState, useEffect, useRef } from "react";
import rulesData from "@/data/diagnosis_rules.json";
import type { DiagnosisAnswers } from "@/utils/diagnosisEngine";

export type StepDefinition =
  | {
      key: string;
      type: "radio";
      legend: string;
      description?: React.ReactNode;
      options: { value: string; label: string; sub?: string }[];
      optional?: boolean;
    }
  | {
      key: string;
      type: "input";
      legend: string;
      description?: React.ReactNode;
      inputLabel: string;
      inputMin?: number;
      inputMax?: number;
      inputPlaceholder?: string;
      checkbox?: { key: string; label: string; sub?: string };
    };

export interface GenericStepWizardProps {
  steps: StepDefinition[];
  answers: any;
  onFieldChange: (key: string, value: any) => void;
  onComplete: () => void;
  initialIndex?: number;
  banner?: React.ReactNode;
}

export interface CareerDiagnosisWizardProps {
  answers: DiagnosisAnswers;
  onAnswerChange: (field: keyof DiagnosisAnswers, value: string) => void;
  onComplete: () => void;
}

export type StepWizardProps = GenericStepWizardProps | CareerDiagnosisWizardProps;

export default function StepWizard(props: StepWizardProps) {
  if ("onAnswerChange" in props) {
    return <CareerDiagnosisWizardImpl {...props} />;
  }
  return <GenericStepWizardImpl {...props} />;
}

/* ─────────────────────────────────────────────────────────────
 * 1. Smore / Toss-Style Interactive 5-Question Career Diagnosis Wizard
 * ───────────────────────────────────────────────────────────── */
function CareerDiagnosisWizardImpl({
  answers,
  onAnswerChange,
  onComplete,
}: CareerDiagnosisWizardProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const questions = rulesData.questions;
  const currentQuestion = questions[currentStepIndex];
  const autoAdvanceTimer = useRef<number | null>(null);

  const totalSteps = questions.length;
  const progressPercent = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) window.clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  const handleSelectOption = (value: string) => {
    const key = currentQuestion.id as keyof DiagnosisAnswers;
    onAnswerChange(key, value);

    if (autoAdvanceTimer.current) {
      window.clearTimeout(autoAdvanceTimer.current);
    }

    setIsTransitioning(true);
    autoAdvanceTimer.current = window.setTimeout(() => {
      setIsTransitioning(false);
      if (currentStepIndex < totalSteps - 1) {
        setCurrentStepIndex((prev) => prev + 1);
        if (containerRef.current) {
          containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        onComplete();
      }
    }, 240);
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      if (autoAdvanceTimer.current) window.clearTimeout(autoAdvanceTimer.current);
      setIsTransitioning(false);
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    const key = currentQuestion.id as keyof DiagnosisAnswers;
    if (answers[key]) {
      if (currentStepIndex < totalSteps - 1) {
        setCurrentStepIndex((prev) => prev + 1);
      } else {
        onComplete();
      }
    }
  };

  const currentAnswerValue = answers[currentQuestion.id as keyof DiagnosisAnswers];

  return (
    <div ref={containerRef} className="w-full max-w-2xl mx-auto scroll-mt-24">
      {/* Top Progress Bar & Step Tracker (Toss / Smore Style) */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2.5">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-600 animate-ping"></span>
            <span className="font-bold text-gray-800 dark:text-gray-200">
              30초 현실 자가진단
            </span>
            <span className="text-gray-400">·</span>
            <span>문항 {currentStepIndex + 1} / {totalSteps}</span>
          </span>
          <span className="text-brand-600 dark:text-brand-400 font-extrabold text-sm">
            {progressPercent}%
          </span>
        </div>
        <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden p-0.5 border border-gray-200/60 dark:border-gray-700/60">
          <div
            className="h-full bg-gradient-to-r from-brand-600 to-indigo-500 rounded-full transition-all duration-300 ease-out shadow-sm"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Main Question Card */}
      <div
        className={`bg-white dark:bg-gray-900 border border-gray-200/90 dark:border-gray-800 rounded-3xl p-6 md:p-8 shadow-lg shadow-gray-200/50 dark:shadow-black/30 transition-all duration-200 ${
          isTransitioning ? "opacity-75 scale-[0.99] translate-y-0.5" : "opacity-100 scale-100 translate-y-0"
        }`}
      >
        {/* Navigation & Question Header */}
        <div className="flex items-center justify-between mb-5">
          <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-brand-50 text-brand-700 dark:bg-brand-950/70 dark:text-brand-300 border border-brand-200 dark:border-brand-800">
            질문 {currentStepIndex + 1}
          </span>

          {currentStepIndex > 0 ? (
            <button
              type="button"
              onClick={handlePrev}
              className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400 transition-colors cursor-pointer py-1.5 px-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <i className="ri-arrow-left-line text-sm" />
              이전 질문으로
            </button>
          ) : (
            <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <i className="ri-shield-check-line" />
              전화번호 불필요 안심 진단
            </span>
          )}
        </div>

        <h2 className="text-xl md:text-2xl font-black text-gray-900 dark:text-gray-50 tracking-tight leading-snug break-keep">
          {currentQuestion.title}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 mb-6 leading-relaxed break-keep">
          {currentQuestion.subtitle}
        </p>

        {/* Smore-style Large Interactive Card Chip Buttons */}
        <div className="space-y-3" role="radiogroup" aria-label={currentQuestion.title}>
          {currentQuestion.options.map((opt) => {
            const isSelected = currentAnswerValue === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelectOption(opt.value)}
                className={`w-full p-4 sm:p-5 rounded-2xl border-2 text-left transition-all duration-150 flex items-start gap-4 group cursor-pointer active:scale-[0.98] ${
                  isSelected
                    ? "border-brand-600 bg-brand-50/80 dark:bg-brand-950/50 ring-2 ring-brand-500/40 shadow-md shadow-brand-600/10"
                    : "border-gray-200/90 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-brand-300 hover:bg-gray-50/90 dark:hover:bg-gray-850 dark:hover:border-gray-700 hover:shadow-sm"
                }`}
              >
                {/* Icon Container with Haptic / Highlight CSS */}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 duration-150 mt-0.5 ${
                    isSelected
                      ? "bg-brand-600 text-white shadow-sm shadow-brand-600/30"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-brand-100 group-hover:text-brand-700 dark:group-hover:bg-brand-900/60 dark:group-hover:text-brand-300"
                  }`}
                >
                  <i className={`${opt.icon} text-lg`}></i>
                </div>

                {/* Text Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                    <span
                      className={`text-base font-extrabold break-keep ${
                        isSelected
                          ? "text-brand-950 dark:text-brand-100"
                          : "text-gray-900 dark:text-gray-100 group-hover:text-brand-900 dark:group-hover:text-brand-200"
                      }`}
                    >
                      {opt.label}
                    </span>
                    {opt.badge && (
                      <span
                        className={`text-[11px] px-2.5 py-0.5 rounded-full font-bold shrink-0 ${
                          isSelected
                            ? "bg-brand-600 text-white"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-brand-100 group-hover:text-brand-700 dark:group-hover:bg-brand-900/40 dark:group-hover:text-brand-300"
                        }`}
                      >
                        {opt.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 leading-relaxed break-keep">
                    {opt.desc}
                  </p>
                </div>

                {/* Selection Radio Dot Indicator */}
                <div className="shrink-0 mt-1">
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                      isSelected
                        ? "border-brand-600 bg-brand-600 text-white shadow-sm"
                        : "border-gray-300 dark:border-gray-700 bg-transparent group-hover:border-brand-400"
                    }`}
                  >
                    {isSelected ? (
                      <i className="ri-check-line text-sm font-black" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-transparent group-hover:bg-gray-200 dark:group-hover:bg-gray-700" />
                    )}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom CTA Bar */}
        <div className="mt-8 pt-5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {currentAnswerValue ? "✓ 선택 완료 (클릭 시 자동 진행)" : "항목을 탭하면 즉시 다음으로 이어집니다"}
          </span>

          <button
            type="button"
            onClick={handleNext}
            disabled={!currentAnswerValue}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-extrabold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md shadow-brand-600/20 cursor-pointer active:scale-95"
          >
            {currentStepIndex === totalSteps - 1 ? "취업 스탯 결과 보기" : "다음 질문"}
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 2. Generic Step Wizard Implementation (for CardForm & NewDealForm)
 * ───────────────────────────────────────────────────────────── */
function GenericStepWizardImpl({
  steps,
  answers,
  onFieldChange,
  onComplete,
  initialIndex = 0,
  banner,
}: GenericStepWizardProps) {
  const [index, setIndex] = useState(initialIndex);
  const indexRef = useRef(initialIndex);
  const advanceTimer = useRef<number | null>(null);

  const setStep = (next: number) => {
    indexRef.current = next;
    setIndex(next);
  };

  const goNext = () => {
    if (advanceTimer.current) {
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
    const next = indexRef.current + 1;
    if (next >= steps.length) {
      onComplete();
      return;
    }
    setStep(next);
  };

  const goBack = () => {
    if (indexRef.current <= 0) return;
    setStep(indexRef.current - 1);
  };

  const scheduleAdvance = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = window.setTimeout(() => {
      goNext();
    }, 280);
  };

  const step = steps[index];
  if (!step) return null;

  const isFirst = index === 0;
  const inputValue = step.type === "input" ? String(answers[step.key] ?? "") : "";
  const canProceedInput = step.type === "input" ? inputValue.trim() !== "" : false;

  return (
    <div className="w-full max-w-2xl mx-auto">
      {isFirst && banner ? banner : null}

      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
          질문 {index + 1} / {steps.length}
        </span>
        {!isFirst && (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors cursor-pointer py-1 px-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <i className="ri-arrow-left-s-line text-base" />
            이전
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200/90 dark:border-gray-800 rounded-3xl p-6 md:p-8 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
          {step.legend}
        </h3>
        {step.description && (
          <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
            {step.description}
          </div>
        )}

        {step.type === "radio" ? (
          <div className="space-y-3">
            {step.options.map((opt) => {
              const isChecked = answers[step.key] === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onFieldChange(step.key, opt.value);
                    scheduleAdvance();
                  }}
                  className={`w-full p-4 sm:p-4.5 rounded-2xl border-2 text-left flex items-center justify-between transition-all cursor-pointer active:scale-[0.98] ${
                    isChecked
                      ? "border-brand-600 bg-brand-50/80 dark:bg-brand-950/40 ring-2 ring-brand-500/40 shadow-sm"
                      : "border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-brand-300 dark:hover:border-gray-700 hover:bg-gray-50/80"
                  }`}
                >
                  <div>
                    <span className="font-extrabold text-sm sm:text-base text-gray-900 dark:text-gray-100">
                      {opt.label}
                    </span>
                    {opt.sub && (
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {opt.sub}
                      </span>
                    )}
                  </div>
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center border-2 shrink-0 ml-3 ${
                      isChecked
                        ? "border-brand-600 bg-brand-600 text-white"
                        : "border-gray-300 dark:border-gray-700"
                    }`}
                  >
                    {isChecked && <i className="ri-check-line text-xs font-bold" />}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
              {step.inputLabel}
            </label>
            <input
              type="number"
              min={step.inputMin}
              max={step.inputMax}
              placeholder={step.inputPlaceholder}
              value={inputValue}
              onChange={(e) => onFieldChange(step.key, e.target.value)}
              className="w-full px-4 py-3.5 border border-gray-300 dark:border-gray-700 rounded-2xl text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all"
            />
            {step.checkbox && (
              <label className="flex items-center gap-2 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!answers[step.checkbox.key]}
                  onChange={(e) => onFieldChange(step.checkbox.key, e.target.checked)}
                  className="w-4 h-4 rounded text-brand-600 cursor-pointer"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  {step.checkbox.label}
                </span>
              </label>
            )}

            <button
              type="button"
              onClick={goNext}
              disabled={!canProceedInput}
              className="w-full py-3.5 rounded-2xl font-bold text-sm text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all mt-4 cursor-pointer active:scale-95"
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
